import {
  startBrowserTracingNavigationSpan,
  updateSpanName,
} from '@sentry/browser'
import * as Sentry from '@sentry/nextjs'

type FrontendViewContext = {
  managerView: string
  role: string
  section: string
}

type SyncFrontendViewContext = FrontendViewContext & {
  lastViewName?: string
}

type FrontendTransactionEvent = {
  type?: string
  transaction?: string
  contexts?: {
    trace?: {
      op?: string
    }
  }
  tags?: Record<string, unknown>
  transaction_info?: Record<string, unknown>
}

const ROLE_STORAGE_KEY = 'selectedRole'
const MANAGER_VIEW_STORAGE_KEY = 'selectedManagerView'
const SECTION_STORAGE_KEY = 'selectedSection'

const ROLE_VALUES = new Set(['Host', 'Server', 'Manager'])
const MANAGER_VIEW_VALUES = new Set(['GuestList', 'Actions'])

const sectionFilter = (section: string) => {
  const normalized = section.trim().toLowerCase()
  if (normalized === 'all') return 'all'
  if (normalized === 'unassigned') return 'unassigned'
  return 'specific'
}

const buildServerViewName = (section: string) => {
  const filter = sectionFilter(section)
  if (filter === 'all') return '/server'
  if (filter === 'unassigned') return '/server/unassigned'
  return '/server/:section'
}

const normalizePathname = (pathname: string | undefined) => {
  if (!pathname) {
    return '/'
  }

  const normalized = pathname.replace(/[?#].*$/, '').replace(/\/+$/, '')

  return normalized || '/'
}

const readPersistedEnum = (
  key: string,
  fallback: string,
  validValues: Set<string>
) => {
  if (typeof window === 'undefined') {
    return fallback
  }

  try {
    const value = window.localStorage.getItem(key)
    return value && validValues.has(value) ? value : fallback
  } catch {
    return fallback
  }
}

const readPersistedSection = () => {
  if (typeof window === 'undefined') {
    return 'All'
  }

  try {
    return window.localStorage.getItem(SECTION_STORAGE_KEY) || 'All'
  } catch {
    return 'All'
  }
}

export const readPersistedFrontendViewContext = (): FrontendViewContext => ({
  managerView: readPersistedEnum(
    MANAGER_VIEW_STORAGE_KEY,
    'GuestList',
    MANAGER_VIEW_VALUES
  ),
  role: readPersistedEnum(ROLE_STORAGE_KEY, 'Host', ROLE_VALUES),
  section: readPersistedSection(),
})

export const buildFrontendViewName = ({
  managerView,
  role,
  section,
}: FrontendViewContext) => {
  switch (role) {
    case 'Manager':
      return managerView === 'Actions'
        ? '/manager/actions'
        : '/manager/guest-list'
    case 'Server':
      return buildServerViewName(section)
    case 'Host':
    default:
      return '/host'
  }
}

export const resolveFrontendTransactionName = (pathname?: string) => {
  const normalizedPathname = normalizePathname(
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/')
  )

  if (normalizedPathname !== '/') {
    return normalizedPathname
  }

  return buildFrontendViewName(readPersistedFrontendViewContext())
}

export const normalizeFrontendTransactionEvent = <
  T extends FrontendTransactionEvent,
>(
  event: T
) => {
  if (typeof window === 'undefined' || event.type !== 'transaction') {
    return event
  }

  const op = event.contexts?.trace?.op
  if (op !== 'pageload' && op !== 'navigation') {
    return event
  }

  const viewContext = readPersistedFrontendViewContext()
  const boundedSectionFilter = sectionFilter(viewContext.section)
  const currentPathname = normalizePathname(window.location.pathname)
  const targetName = resolveFrontendTransactionName(currentPathname)

  if (
    !event.transaction ||
    event.transaction === '/' ||
    event.transaction === currentPathname
  ) {
    event.transaction = targetName
  }

  event.tags = {
    ...event.tags,
    'frontend.manager_view': viewContext.managerView,
    'frontend.role': viewContext.role,
    'frontend.section_filter': boundedSectionFilter,
  }
  event.transaction_info = {
    ...event.transaction_info,
    source: 'route',
  }

  return event
}

const syncScopeContext = ({
  managerView,
  role,
  section,
  viewName,
}: FrontendViewContext & { viewName: string }) => {
  const boundedSectionFilter = sectionFilter(section)
  const scope = Sentry.getCurrentScope()
  scope.setTransactionName(viewName)
  scope.setTag('frontend.role', role)
  scope.setTag('frontend.section_filter', boundedSectionFilter)
  scope.setTag('frontend.manager_view', managerView)
  scope.setContext('frontend_view', {
    managerView,
    role,
    sectionFilter: boundedSectionFilter,
    viewName,
  })
}

export const syncFrontendViewInstrumentation = ({
  lastViewName,
  managerView,
  role,
  section,
}: SyncFrontendViewContext) => {
  const viewName = buildFrontendViewName({ managerView, role, section })

  if (typeof window === 'undefined') {
    return viewName
  }

  syncScopeContext({ managerView, role, section, viewName })

  const client = Sentry.getClient()
  if (!client) {
    return viewName
  }

  const activeSpan = Sentry.getActiveSpan()
  const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined
  const rootSpanJson = rootSpan ? Sentry.spanToJSON(rootSpan) : undefined

  if (
    !lastViewName &&
    rootSpan &&
    rootSpanJson &&
    (rootSpanJson.op === 'pageload' || rootSpanJson.op === 'navigation')
  ) {
    updateSpanName(rootSpan, viewName)
    rootSpan.setAttribute(Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'route')
    return viewName
  }

  if (lastViewName === viewName) {
    return viewName
  }

  const syntheticUrl = new URL(viewName, window.location.origin).toString()

  startBrowserTracingNavigationSpan(
    client,
    {
      name: viewName,
      op: 'navigation',
      attributes: {
        [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'route',
      },
    },
    {
      url: syntheticUrl,
    }
  )

  return viewName
}
